import { FC } from "react";
import { IonCol, IonItem } from "@ionic/react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { orderBy } from "lodash";
import moment from "moment";
import { AggregatedStats, MatchPhone } from "refsix-js-models";
import {
    BarChartComponent,
    HeadlineNumbersComponent,
} from "../../../components";
import ListOfTopComponent, {
    LIST_OF_TOP_DATE_FORMAT,
} from "../../../components/ListOfTopComponent";
import { ScorzaCard } from "../../../components/stats/ScorzaCard";
import { RootState } from "../../../redux/store";
import { createDefaultFiltersSetting } from "../../filters/filtersHelper";
import { MatchType } from "../../filters/enums";
import { lastFinishedMatches } from "../../../services/matchService";
import {
    contributionLabel,
    getGoalsBarOptions,
    getGoalsChartData,
    goalsAndAssists,
    goalsPerMatch,
} from "./goalsHelpers";

const LAST_MATCHES = 10;
const BIGGEST_CONTRIBUTIONS = 5;

interface GoalsProps {
    matches: MatchPhone[];
    aggregatedStats?: AggregatedStats;
}

/** The running total of goals and assists the player has claimed (SCORZA-110). */
export const Goals: FC<GoalsProps> = ({ matches, aggregatedStats }) => {
    const { t } = useTranslation();
    const filters = useSelector(
        (state: RootState) =>
            state.filters.filterResults ||
            createDefaultFiltersSetting(MatchType.Result)
    );
    const goals = aggregatedStats?.myGoalsTotal || 0;
    const assists = aggregatedStats?.myAssistsTotal || 0;

    const last10 = lastFinishedMatches(matches, filters, LAST_MATCHES);

    const biggest = orderBy(
        matches.filter((match) => {
            const [g, a] = goalsAndAssists(match);
            return g + a > 0;
        }),
        [(match) => goalsAndAssists(match).reduce((a, b) => a + b), "date"],
        ["desc", "desc"]
    ).slice(0, BIGGEST_CONTRIBUTIONS);

    return (
        <>
            <ScorzaCard title={t("stats.stats.goals.title")}>
                <HeadlineNumbersComponent
                    testIdsPrefix="goal-claims"
                    data={[
                        goals,
                        assists,
                        goalsPerMatch(
                            goals,
                            assists,
                            aggregatedStats?.matchesTotal
                        ),
                    ]}
                    labels={[
                        t("editMatch.claimRack.goals"),
                        t("editMatch.claimRack.assists"),
                        t("stats.stats.goals.perMatch"),
                    ]}
                />
                <IonItem lines="none">
                    <h3>{t("stats.stats.last10Matches")}</h3>
                </IonItem>
                <IonItem lines="none">
                    <IonCol>
                        <BarChartComponent
                            data={getGoalsChartData(last10, t)}
                            barChartOptions={getGoalsBarOptions(t)}
                            testId="goal-claims-chart"
                        />
                    </IonCol>
                </IonItem>
            </ScorzaCard>
            <ScorzaCard title={t("stats.stats.goals.biggestContributions")}>
                {biggest.length === 0 && (
                    <IonItem lines="none" data-testid="goal-claims-empty">
                        {t("stats.stats.goals.empty")}
                    </IonItem>
                )}
                {biggest.map((match) => {
                    const [g, a] = goalsAndAssists(match);
                    return (
                        <ListOfTopComponent
                            key={match._id}
                            matchId={match._id}
                            stat={contributionLabel(g, a)}
                            teamNames={`${match.homeTeam} v ${match.awayTeam}`}
                            date={moment(match.date).format(
                                LIST_OF_TOP_DATE_FORMAT
                            )}
                            testId="goal-claims-match"
                            valueSize="medium"
                        />
                    );
                })}
            </ScorzaCard>
        </>
    );
};
